import { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api';

type PrivacySection = { title: string; body: string };

const DEFAULT_INSTAGRAM_URL = 'https://www.instagram.com/foisenegal.app?igsh=MXJsbnl5NGR1MmVrZg==';

type AppContent = {
  about_title: string;
  about_text: string;
  contact_title: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  website_label: string;
  website_url: string;
  privacy_title: string;
  privacy_updated_at: string;
  privacy_intro: string;
  privacy_sections: PrivacySection[];
  professional_title: string;
  professional_subtitle: string;
  professional_section_title: string;
  professional_intro: string;
  vendor_title: string;
  vendor_subtitle: string;
  vendor_url: string;
  organizer_title: string;
  organizer_subtitle: string;
  organizer_url: string;
  facebook_url: string;
  instagram_url: string;
  youtube_url: string;
  tiktok_url: string;
  footer_text: string;
  latest_mobile_version: string;
  latest_mobile_version_code: string;
  app_update_title: string;
  app_update_message: string;
  app_update_url: string;
};

const emptyContent: AppContent = {
  about_title: '',
  about_text: '',
  contact_title: '',
  contact_email: '',
  contact_phone: '',
  contact_address: '',
  website_label: '',
  website_url: '',
  privacy_title: '',
  privacy_updated_at: '',
  privacy_intro: '',
  privacy_sections: [],
  professional_title: '',
  professional_subtitle: '',
  professional_section_title: '',
  professional_intro: '',
  vendor_title: '',
  vendor_subtitle: '',
  vendor_url: '',
  organizer_title: '',
  organizer_subtitle: '',
  organizer_url: '',
  facebook_url: '',
  instagram_url: DEFAULT_INSTAGRAM_URL,
  youtube_url: '',
  tiktok_url: '',
  footer_text: '',
  latest_mobile_version: '1.0.0',
  latest_mobile_version_code: '2',
  app_update_title: 'Mise à jour disponible',
  app_update_message: 'Une nouvelle version de Foi Sénégal est disponible. Mettez l’application à jour pour profiter des dernières améliorations.',
  app_update_url: 'https://play.google.com/store/apps/details?id=org.foisenegal.mobile&pcampaignid=web_share',
};

export default function AdminAppContent() {
  const [form, setForm] = useState<AppContent>(emptyContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [sectionDeleteTarget, setSectionDeleteTarget] = useState<{ index: number; title: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const content = await apiRequest<AppContent>('/app-content');
        setForm({
          ...content,
          instagram_url: content.instagram_url || DEFAULT_INSTAGRAM_URL,
        });
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  const setField = (field: keyof AppContent, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setSection = (index: number, field: keyof PrivacySection, value: string) => {
    setForm((current) => ({
      ...current,
      privacy_sections: current.privacy_sections.map((section, sectionIndex) => (
        sectionIndex === index ? { ...section, [field]: value } : section
      )),
    }));
  };

  const confirmSectionDelete = () => {
    if (!sectionDeleteTarget) return;

    setForm((current) => ({
      ...current,
      privacy_sections: current.privacy_sections.filter((_, itemIndex) => itemIndex !== sectionDeleteTarget.index),
    }));
    setSectionDeleteTarget(null);
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await apiRequest<AppContent>('/admin/app-content', {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      setForm(response);
      setMessage('Contenu mis à jour avec succès. Une notification est envoyée si la version mobile a changé.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Impossible de sauvegarder le contenu.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-gray-100 bg-white p-6 text-sm text-gray-400">Chargement du contenu...</div>;
  }

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-100 bg-white p-5">
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Contenu de l'application</h3>
            <p className="text-xs text-gray-400">Confidentialité, footer/contact et espace professionnel mobile.</p>
          </div>
          <button onClick={save} disabled={saving} className="rounded-xl bg-navy px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
        {message ? <p className="mb-4 rounded-xl bg-gold-100 px-4 py-3 text-sm font-semibold text-gold-dark">{message}</p> : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Titre à propos" value={form.about_title} onChange={(value) => setField('about_title', value)} />
          <Field label="Titre contact" value={form.contact_title} onChange={(value) => setField('contact_title', value)} />
          <Textarea label="Texte à propos" value={form.about_text} onChange={(value) => setField('about_text', value)} />
          <div className="grid gap-3">
            <Field label="Email contact" value={form.contact_email} onChange={(value) => setField('contact_email', value)} />
            <Field label="Téléphone contact" value={form.contact_phone} onChange={(value) => setField('contact_phone', value)} />
            <Field label="Adresse contact" value={form.contact_address} onChange={(value) => setField('contact_address', value)} />
            <Field label="Libellé site web" value={form.website_label} onChange={(value) => setField('website_label', value)} />
            <Field label="Lien site web" value={form.website_url} onChange={(value) => setField('website_url', value)} />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-5">
        <h3 className="mb-4 text-sm font-bold text-gray-900">Espace professionnel mobile</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Titre espace pro" value={form.professional_title} onChange={(value) => setField('professional_title', value)} />
          <Field label="Sous-titre espace pro" value={form.professional_subtitle} onChange={(value) => setField('professional_subtitle', value)} />
          <Field label="Titre section" value={form.professional_section_title} onChange={(value) => setField('professional_section_title', value)} />
          <Field label="Texte introduction" value={form.professional_intro} onChange={(value) => setField('professional_intro', value)} />
          <Field label="Titre bouton vendeur" value={form.vendor_title} onChange={(value) => setField('vendor_title', value)} />
          <Field label="Sous-titre bouton vendeur" value={form.vendor_subtitle} onChange={(value) => setField('vendor_subtitle', value)} />
          <Field label="Lien vendeur" value={form.vendor_url} onChange={(value) => setField('vendor_url', value)} />
          <Field label="Titre bouton organisateur" value={form.organizer_title} onChange={(value) => setField('organizer_title', value)} />
          <Field label="Sous-titre bouton organisateur" value={form.organizer_subtitle} onChange={(value) => setField('organizer_subtitle', value)} />
          <Field label="Lien organisateur" value={form.organizer_url} onChange={(value) => setField('organizer_url', value)} />
          <Field label="Texte footer mobile" value={form.footer_text} onChange={(value) => setField('footer_text', value)} />
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-5">
        <h3 className="mb-4 text-sm font-bold text-gray-900">Liens réseaux sociaux mobile</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Facebook" value={form.facebook_url} onChange={(value) => setField('facebook_url', value)} />
          <Field label="Instagram" value={form.instagram_url} onChange={(value) => setField('instagram_url', value)} />
          <Field label="YouTube" value={form.youtube_url} onChange={(value) => setField('youtube_url', value)} />
          <Field label="TikTok" value={form.tiktok_url} onChange={(value) => setField('tiktok_url', value)} />
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-5">
        <h3 className="mb-4 text-sm font-bold text-gray-900">Notification de mise à jour mobile</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Dernière version mobile" value={form.latest_mobile_version} onChange={(value) => setField('latest_mobile_version', value)} />
          <Field label="Code version mobile" value={form.latest_mobile_version_code} onChange={(value) => setField('latest_mobile_version_code', value)} />
          <Field label="Titre notification" value={form.app_update_title} onChange={(value) => setField('app_update_title', value)} />
          <Field label="Lien mise à jour" value={form.app_update_url} onChange={(value) => setField('app_update_url', value)} />
          <Textarea label="Message notification" value={form.app_update_message} onChange={(value) => setField('app_update_message', value)} />
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-5">
        <h3 className="mb-4 text-sm font-bold text-gray-900">Politique de confidentialité mobile</h3>
        <div className="mb-4 grid gap-4 lg:grid-cols-2">
          <Field label="Titre" value={form.privacy_title} onChange={(value) => setField('privacy_title', value)} />
          <Field label="Dernière mise à jour" value={form.privacy_updated_at} onChange={(value) => setField('privacy_updated_at', value)} />
          <Textarea label="Introduction" value={form.privacy_intro} onChange={(value) => setField('privacy_intro', value)} />
        </div>
        <div className="space-y-3">
          {form.privacy_sections.map((section, index) => (
            <div key={index} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="mb-3 flex justify-end">
                <button
                  onClick={() => setSectionDeleteTarget({ index, title: section.title || `Section ${index + 1}` })}
                  className="rounded-lg px-2 py-1 text-xs font-semibold text-red-500 hover:bg-red-50"
                >
                  Supprimer
                </button>
              </div>
              <Field label="Titre section" value={section.title} onChange={(value) => setSection(index, 'title', value)} />
              <Textarea label="Texte section" value={section.body} onChange={(value) => setSection(index, 'body', value)} />
            </div>
          ))}
        </div>
        <button
          onClick={() => setForm((current) => ({ ...current, privacy_sections: [...current.privacy_sections, { title: '', body: '' }] }))}
          className="mt-4 rounded-xl border border-cream-dark px-4 py-2 text-sm font-semibold text-navy hover:bg-cream"
        >
          Ajouter une section
        </button>
      </div>

      {sectionDeleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4 py-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-500">
              <i className="ri-delete-bin-line text-lg"></i>
            </div>
            <h3 className="text-lg font-bold text-navy">Confirmer la suppression</h3>
            <p className="mt-2 text-sm leading-6 text-gray-500">
              Cette section sera retirée de la politique de confidentialité mobile après sauvegarde.
            </p>
            <div className="mt-4 rounded-xl bg-gray-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Section</p>
              <p className="mt-1 text-sm font-bold text-gray-800">{sectionDeleteTarget.title}</p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setSectionDeleteTarget(null)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmSectionDelete}
                className="rounded-xl bg-red-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-600"
              >
                Oui, supprimer
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-gray-500">{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-xl border border-cream-dark px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold/30" />
    </label>
  );
}

function Textarea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-gray-500">{label}</span>
      <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={5} className="w-full rounded-xl border border-cream-dark px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold/30" />
    </label>
  );
}
